import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "d-flex flex-column gap-3 justify-content-between" }

import { ComputedRef, computed, ref, Ref } from 'vue'

  // Lodash
  import _ from 'lodash'

  // Types
  import {
    BookingSummary,
    TravellerSummary,
  } from '@/services/BookingService.interface'

  // Components
  import SlidingPanel from '@/components/UI/SlidingPanel.vue'
  import Input from '@/components/UI/Input.vue'
  import DatePicker from '@/components/UI/DatePicker.vue'
  import InputFieldError from '@/components/UI/InputFieldError.vue'

  // Store
  import { useStore } from '@/store'
  import { useI18n } from 'vue-i18n'
  import { getAge, validateName } from '@/utils'

  // Props
  interface Props {
    selectedTraveller: TravellerSummary | null
    panelTitle: string
    panelId: string
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'EditTravellerPanel',
  props: {
    selectedTraveller: {},
    panelTitle: {},
    panelId: {}
  },
  setup(__props: any) {

  // Vue
  const store = useStore()

  // Translations
  const { t } = useI18n()

  // Utils
  const props = __props

  // Refs
  const updatedTravellerData: Ref<Partial<TravellerSummary>> = ref({
    firstName: props.selectedTraveller?.firstName || '',
    lastName: props.selectedTraveller?.lastName || '',
    birthday: props.selectedTraveller?.birthday || '',
  })

  const submitted: Ref<boolean> = ref(false)

  const isPanelOpen: ComputedRef<boolean> = computed(() => {
    const checkIsOpen = store.getters['modal/isOpen']
    return checkIsOpen(props.panelId)
  })

  // Store values
  const isLoading: ComputedRef<boolean> = computed(() =>
    store.getters['booking/isLoading']('updateTraveller')
  )

  const error: ComputedRef<string> = computed(() =>
    store.getters['booking/getError']('updateTraveller')
  )

  const selectedBooking: ComputedRef<BookingSummary> = computed(() => {
    return store.getters['booking/selectedBooking']
  })

  const travellers: ComputedRef<TravellerSummary[]> = computed(() => {
    return selectedBooking.value.travellers
  })

  // Computed

  const invalidFields: ComputedRef<{
    firstName: boolean
    lastName: boolean
    birthday: boolean
  }> = computed(() => {
    return {
      firstName: !validateName(updatedTravellerData.value.firstName),
      lastName: !validateName(updatedTravellerData.value.lastName),
      birthday: !atLeastOneAdult.value,
    }
  })

  const missingMandatoryInfo: ComputedRef<boolean> = computed(() => {
    return (
      invalidFields.value.firstName ||
      invalidFields.value.lastName ||
      invalidFields.value.birthday
    )
  })

  const showErrors: ComputedRef<boolean> = computed(() => {
    return (
      submitted.value && (missingMandatoryInfo.value || !atLeastOneAdult.value)
    )
  })

  // Get the ages of travellers with the current age selected in the form
  const ages: ComputedRef<string[]> = computed(() => {
    if (!props.selectedTraveller) return []

    const updatedTravellers = travellers.value.map(
      (traveller: TravellerSummary) =>
        traveller.id === props.selectedTraveller?.id
          ? { ...traveller, ...updatedTravellerData.value }
          : traveller
    )

    const departureDate = selectedBooking.value?.departureDate
    return updatedTravellers.map((traveller: TravellerSummary) =>
      getAge(traveller.birthday, departureDate)
    )
  })

  // Check if at least one of the travellers is 18 and over, if not do not allow the user to select under 18
  const atLeastOneAdult: ComputedRef<boolean> = computed(() => {
    return ages.value.includes('18+')
  })

  // Methods
  const onClose = (): void => {
    store.commit('modal/close', props.panelId)
  }

  const onConfirm = async (): Promise<void> => {
    try {
      submitted.value = true
      if (showErrors.value) return
      await store.dispatch('booking/updateTraveller', {
        ...props.selectedTraveller,
        ...updatedTravellerData.value,
      })
      onClose()
    } catch (error) {
      console.error('Failed to update traveller:', error)
    }
  }

  const updateFields = (field: keyof TravellerSummary, value: string): void => {
    console.log(" updateFields ~ value:", value)
    if (field === 'birthday') {
      updatedTravellerData.value.birthday = value
    } else if (field === 'firstName') {
      updatedTravellerData.value.firstName = value
    } else if (field === 'lastName') {
      updatedTravellerData.value.lastName = value
    }
  }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SlidingPanel, {
    isPanelVisible: isPanelOpen.value,
    onClose: onClose,
    "on-confirm": onConfirm,
    "close-title": _ctx.panelTitle,
    "display-confirm-button": true,
    "button-disabled": showErrors.value || isLoading.value,
    "is-loading": isLoading.value,
    "has-overlay": ""
  }, {
    default: _withCtx(() => [
      _createElementVNode("section", _hoisted_1, [
        _createElementVNode("div", null, [
          _createVNode(Input, {
            id: "first-name",
            label: _unref(t)('travellers.firstName'),
            placeholder: _unref(t)('travellers.firstName'),
            value: updatedTravellerData.value.firstName || '',
            onInput: _cache[0] || (_cache[0] = value => updateFields('firstName', value)),
            error: _unref(t)('errorMessages.fieldMandatory'),
            "show-error": invalidFields.value.firstName,
            readOnly: isLoading.value
          }, null, 8, ["label", "placeholder", "value", "error", "show-error", "readOnly"]),
          _createVNode(Input, {
            id: "last-name",
            label: _unref(t)('travellers.lastName'),
            placeholder: _unref(t)('travellers.lastName'),
            value: updatedTravellerData.value.lastName || '',
            onInput: _cache[1] || (_cache[1] = value => updateFields('lastName', value)),
            "show-error": invalidFields.value.lastName,
            error: _unref(t)('errorMessages.fieldMandatory'),
            readOnly: isLoading.value
          }, null, 8, ["label", "placeholder", "value", "show-error", "error", "readOnly"]),
          _createVNode(DatePicker, {
            date: updatedTravellerData.value.birthday || '',
            onDateChange: _cache[2] || (_cache[2] = value => updateFields('birthday', value)),
            "show-error": showErrors.value && invalidFields.value.birthday,
            error: _unref(t)('errorMessages.atLeastOne18')
          }, null, 8, ["date", "show-error", "error"]),
          (error.value)
            ? (_openBlock(), _createBlock(InputFieldError, {
                key: 0,
                text: _unref(t)('errorMessages.updateTraveller'),
                class: "px-4"
              }, null, 8, ["text"]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }, 8, ["isPanelVisible", "close-title", "button-disabled", "is-loading"]))
}
}

})